import { chunk, groupBy } from "lodash";
import cuid from "cuid";

import type { Acknowledgement, AcknowledgementSubject, AcknowledgementWrite } from "@storyboard/web-client";
import { serverNow, writeAcknowledgementLog } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import type { AnchorAcknowledgementSubject } from "@/models/acknowledgement/acknowledgementStore";
import { removeAcknowledgements, upsertAcknowledgements } from "@/models/acknowledgement/acknowledgementStore";
import { sendOptimisticBatch } from "@/models/sync/optimisticBatch";
import { store } from "@/store/store";

const log = createLogger("events");

/** The most entries one write may carry: the route's `ACKNOWLEDGEMENT_WRITE_LIMIT`, which the wire does not export. */
const ACKNOWLEDGEMENT_WRITE_BATCH = 500;

/** Who is acknowledging, in which workspace: stamped on the optimistic rows as the server stamps the stored ones. */
export type AcknowledgementWriter = { workspaceId: string; actorWorkspaceMembershipId: string | null };

/** What an action names before it gets an id: one target and whether it is handled or taken back. */
export type AcknowledgementIntent =
  | { subject: "feed"; action: AcknowledgementWrite["action"]; feedId: string; through?: string }
  | { subject: "feed_item"; action: AcknowledgementWrite["action"]; feedItemId: string }
  | { subject: AnchorAcknowledgementSubject; action: AcknowledgementWrite["action"]; anchorId: string }
  | { subject: "task"; action: AcknowledgementWrite["action"]; taskId: string };

/**
 * Mints the write for an intent: a fresh id, which is the write's idempotency key and the row's identity.
 *
 * A channel unacknowledge drops the `through` it carries for undo; the log stores none on an unacknowledge.
 */
export function acknowledgementWriteFor(intent: AcknowledgementIntent): AcknowledgementWrite {
  if (intent.subject === "feed" && intent.action === "unacknowledged") {
    const { through: _through, ...write } = intent;
    return { ...write, id: cuid() };
  }
  return { ...intent, id: cuid() };
}

/**
 * The row the store holds until the server's copy arrives: the write, stamped as the server would stamp it.
 *
 * The instant is the server's clock as the client estimates it, so the row folds in the right place among its
 * neighbours; the server's own `createdAt` replaces it when the delta echoes the row back under the same id.
 */
export function optimisticRowOf(write: AcknowledgementWrite, writer: AcknowledgementWriter): Acknowledgement | null {
  const shared = {
    id: write.id,
    workspaceId: writer.workspaceId,
    actorWorkspaceMembershipId: writer.actorWorkspaceMembershipId,
    action: write.action,
    createdAt: serverNow().toISOString(),
  };
  switch (write.subject) {
    case "feed":
      return {
        ...shared,
        subject: "feed",
        feedId: write.feedId,
        through: write.action === "acknowledged" ? (write.through ?? null) : null,
      };
    case "feed_item": {
      const feedId = store.get("item", write.feedItemId)?.feedId;
      return feedId ? { ...shared, subject: "feed_item", feedItemId: write.feedItemId, feedId } : null;
    }
    case "task":
      return { ...shared, subject: "task", taskId: write.taskId };
    default:
      return { ...shared, subject: write.subject, anchorId: write.anchorId } as Acknowledgement;
  }
}

/**
 * Records acknowledgements: into the store at once, so the red dots move on the keystroke, then to the server.
 *
 * One request per subject, in parallel, split at the route's batch limit. An entry the server drops (its target is gone or foreign) is taken back out of
 * the store; a request that fails outright takes its whole batch back out, so the page never shows an acknowledgement
 * the log does not hold.
 */
export function recordAcknowledgements(writer: AcknowledgementWriter, writes: readonly AcknowledgementWrite[]): void {
  if (writes.length === 0) return;
  const optimistic = writes.flatMap((write) => optimisticRowOf(write, writer) ?? []);
  upsertAcknowledgements(optimistic);

  for (const [subject, all] of Object.entries(groupBy(writes, (write) => write.subject))) {
    for (const batch of chunk(all, ACKNOWLEDGEMENT_WRITE_BATCH)) {
      sendBatch(writer, subject as AcknowledgementSubject, batch);
    }
  }
}

/** One request, at most the route's limit: dropped entries leave the store, and a failed request takes all back. */
function sendBatch(
  writer: AcknowledgementWriter,
  subject: AcknowledgementSubject,
  acknowledgements: AcknowledgementWrite[],
): void {
  sendOptimisticBatch({
    ids: acknowledgements.map((write) => write.id),
    send: () => writeAcknowledgementLog(transport, { workspaceId: writer.workspaceId, subject, acknowledgements }),
    takeBack: (ids) => removeAcknowledgements(subject, ids),
    onFailure: (error) =>
      log.warn("Could not record acknowledgements; taking them back", {
        subject,
        count: acknowledgements.length,
        error,
      }),
  });
}
